import { useState } from 'react'
import { useMount } from '~/shared/hooks/useMount'
import type { FC } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import SearchIcon from '@mui/icons-material/Search'
import { InputAdornment, Box, Grid, TextField } from '@mui/material'
import { CPTCode } from './types'

import { LocationTypeSearchCategory, SpecialtyGroupSearchCategory } from './ProviderSearchContent'
import { EditField } from '~/shared/ui'
import { SpecialtyDropdownOption } from '~/entities/steerage'

interface FacilitySearchOptionsPickerProps {
  locationTypes: SpecialtyDropdownOption[]
  onSelect: (
    locationType: LocationTypeSearchCategory | null,
    specialtyGroup: SpecialtyGroupSearchCategory | null,
    npi: string | null,
    providerName: string | null,
    gender: string | null,
    facilityName: string | null,
    procedureCode: CPTCode | null,
    cptCode: CPTCode | null,
  ) => void
  disabled: boolean
  loading: boolean
  selectedLocationType: LocationTypeSearchCategory | null
  selectedFacilityName: string | null
  setSearchRadius: (val: number) => void
}

export const FacilitySearchOptionsPicker: FC<FacilitySearchOptionsPickerProps> = (
  props: FacilitySearchOptionsPickerProps,
) => {
  const [locationType, setLocationType] = useState<LocationTypeSearchCategory | null>(
    props.selectedLocationType,
  )
  const [searchString, setSearchString] = useState<string>('')
  const [facilityName, setFacilityName] = useState<string | null>(props.selectedFacilityName)
  const { onSelect } = props

  let facilityLabels: LocationTypeSearchCategory[] = props.locationTypes?.map(locationType => {
    return {
      id: locationType.id,
      label: locationType.label,
      type: 'Facility',
      category: 'Facility',
    }
  })
  facilityLabels = facilityLabels || []

  const setSearchTerms = (
    event: React.SyntheticEvent,
    newValue: LocationTypeSearchCategory | null,
  ) => {
    let locationTypeFromValue: LocationTypeSearchCategory | null = null
    if (newValue?.type === 'Facility') {
      locationTypeFromValue = newValue
    }
    setLocationType(locationTypeFromValue)
    onSelect(locationTypeFromValue, null, null, null, null, facilityName, null, null)
  }

  // Publishes the initial selection once, so the parent can seed a search from the URL. Every
  // later change already calls `onSelect` from the handler that made it.
  useMount(() => {
    onSelect(locationType, null, null, null, null, facilityName, null, null)
  })

  const setFacilityNameParam = (event: React.ChangeEvent<HTMLInputElement>) => {
    const facilityNameFromEvent = event.target.value
    setFacilityName(facilityNameFromEvent)
    onSelect(locationType, null, null, null, null, facilityNameFromEvent, null, null)

    // set distance to 25miles if it's name search else 10miles for other searches
    props?.setSearchRadius(facilityNameFromEvent ? 25 : 10)
  }

  return (
    <Box style={{ gap: 20 }} sx={{ display: 'flex', maxWidth: 640, flexDirection: 'column' }}>
      <Grid size={6}>
        <Autocomplete
          value={locationType}
          onChange={setSearchTerms}
          inputValue={searchString}
          onInputChange={(event, newSearchString) => {
            setSearchString(newSearchString)
          }}
          id="facility-picker"
          options={facilityLabels}
          getOptionLabel={option => `${option.label}`}
          sx={{ width: '100%' }}
          size="small"
          disabled={props.disabled}
          loading={props.loading}
          forcePopupIcon={false}
          renderOption={({ key: _, ...props }, option: LocationTypeSearchCategory) => (
            <li {...props} key={option.id}>
              {option.label}
            </li>
          )}
          renderInput={params => {
            return (
              <TextField
                {...params}
                label="Facility Type"
                variant="outlined"
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )
          }}
        />
      </Grid>
      <EditField
        label="Facility Name"
        type="text"
        name="facilityName"
        value={facilityName}
        onChange={setFacilityNameParam}
        disabled={props.disabled}
        slotProps={{ inputLabel: { shrink: true } }}
      />
    </Box>
  )
}
